/**
 * 《qqq的项目》用戶端 `/member/*`（fetch）。開發環境由 vue.config devServer.proxy 轉發。
 * 啟用：.env.local 設 VUE_APP_USE_MEMBER_REST=1
 */

let mobiForceLogoutHandler = null
let mobiForceLogoutPending = false

function isLogoutStatus(payload) {
  if (!payload || typeof payload !== 'object') return false
  const st = payload.status ?? payload.Status ?? payload.state ?? payload.State
  const s = String(st || '').trim().toLowerCase()
  if (s === 'logout') return true
  if (s !== '' && s !== 'success') {
    const msg = (payload.msg || payload.message || payload.Message || '').toString().toLowerCase()
    const keywords = ['登录超时', '登录已失效', '登录已过期', '重新登录', '登录失效', '登录状态已失效', 'session expired']
    return keywords.some((k) => msg.includes(k))
  }
  return false
}

function readLogoutMessage(payload) {
  if (!payload || typeof payload !== 'object') return ''
  return String(payload.msg || payload.message || payload.Message || '登錄已失效，是否重新登錄？').trim()
}

export function setMobiMemberForceLogoutHandler(fn) {
  mobiForceLogoutHandler = typeof fn === 'function' ? fn : null
}

function triggerMobiForceLogout(payload, requestPath) {
  if (!mobiForceLogoutHandler || mobiForceLogoutPending) return
  if (String(requestPath || '').toLowerCase().includes('/member/login')) return
  if (!isLogoutStatus(payload)) return
  mobiForceLogoutPending = true
  try {
    mobiForceLogoutHandler(readLogoutMessage(payload))
  } catch (e) {
    void e
  } finally {
    const w = typeof window !== 'undefined' ? window : null
    if (w && w.setTimeout) {
      w.setTimeout(() => {
        mobiForceLogoutPending = false
      }, 1500)
    } else {
      mobiForceLogoutPending = false
    }
  }
}

/**
 * 非 parseJson 路徑（如手動 fetch）在拿到 JSON 後調用，與會員接口 status=logout 行為一致
 */
export function notifyMobiMemberLogoutIfNeeded(payload, requestPath) {
  triggerMobiForceLogout(payload, requestPath)
}

function base() {
  try {
    const w = typeof window !== 'undefined' ? window : null
    if (w && w.__VUE_APP_MOBI_SAME_ORIGIN__ === true) return ''
    const rt = w && w.__VUE_APP_MEMBER_API_BASE__
    if (rt != null && String(rt).trim() !== '') return String(rt).trim()
  } catch (_) {}
  const fromEnv =
    (typeof process !== 'undefined' && process.env && process.env.VUE_APP_MEMBER_API_BASE) || ''
  if (fromEnv && String(fromEnv).trim() !== '') return String(fromEnv).trim()
  return ''
}

function joinUrl(path) {
  const b = base().replace(/\/$/, '')
  const p = path.startsWith('/') ? path : '/' + path
  return (b || '') + p
}

function parseJson(res, data, requestPath) {
  const d = data
  if (d == null) return { ok: false, data: null, msg: '空響應', raw: d }
  if (res && (res.status === 401 || res.status === 403)) {
    triggerMobiForceLogout({ status: 'logout', msg: '登錄已失效，請重新登錄。' }, requestPath)
  }
  const ok = d.status === 'success' || d.Status === 'success'
  const msg = d.msg || d.message || d.Message || ''
  let payload = d.data !== undefined ? d.data : d.Data
  if (ok && payload === undefined) {
    const rest = { ...d }
    delete rest.status
    delete rest.Status
    delete rest.msg
    delete rest.message
    delete rest.Message
    delete rest.data
    delete rest.Data
    if (Object.keys(rest).length) payload = rest
  }
  if (d && typeof d === 'object') triggerMobiForceLogout(d, requestPath)
  return { ok, data: payload, msg, raw: d }
}

function formBody(obj) {
  const p = new URLSearchParams()
  Object.keys(obj || {}).forEach((k) => {
    let v = obj[k]
    if (v === undefined || v === null) return
    if (typeof v === 'object' && !(v instanceof Date)) v = JSON.stringify(v)
    p.set(k, String(v))
  })
  return p
}

async function mget(path) {
  const url = joinUrl(path)
  const res = await fetch(url, { credentials: 'include' })
  const data = await res.json().catch(() => ({}))
  return parseJson(res, data, path)
}

async function mpost(path, body) {
  const url = joinUrl(path)
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formBody(body),
  })
  const data = await res.json().catch(() => ({}))
  return parseJson(res, data, path)
}

function ok(pr, err) {
  if (!pr.ok) throw new Error(pr.msg || err)
  return pr.data
}

/** 將接口返回的 Base64（或 data URL）轉為圖片 Blob */
function base64ImageToBlob(input) {
  let str = String(input == null ? '' : input).trim()
  let mime = 'image/jpeg'
  if (str.startsWith('data:')) {
    const m = str.match(/^data:([^;]+);base64,(.+)$/s)
    if (m) {
      mime = m[1] || mime
      str = m[2].replace(/\s/g, '')
    }
  } else {
    str = str.replace(/\s/g, '')
  }
  if (!str) throw new Error('驗證碼 Base64 為空')
  const binary = atob(str)
  const len = binary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
  if (bytes[0] === 0x89 && bytes[1] === 0x50) mime = 'image/png'
  else if (bytes[0] === 0xff && bytes[1] === 0xd8) mime = 'image/jpeg'
  else if (bytes[0] === 0x47 && bytes[1] === 0x49) mime = 'image/gif'
  return new Blob([bytes], { type: mime })
}

/** img 直連地址（失敗回退；需與接口同域或已配置代理） */
export function getMemberCaptchaImgSrc() {
  return joinUrl('/member/code') + '?_t=' + Date.now()
}

/**
 * GET /member/code，返回圖片 Blob。
 * 兼容 JSON：`{ status, data: { code: "<base64>" } }` 與直出 image/*
 */
export async function fetchMemberCaptchaBlob() {
  const url = joinUrl('/member/code') + '?_t=' + Date.now()
  const res = await fetch(url, { credentials: 'include' })
  const ct = (res.headers.get('content-type') || '').toLowerCase()
  const blob = await res.blob()
  if (ct.indexOf('image/') !== -1) {
    return blob
  }
  const text = await blob.text()
  let parsed
  try {
    parsed = JSON.parse(text)
  } catch (e) {
    throw new Error('驗證碼數據無法解析')
  }
  const pr = parseJson(res, parsed, '/member/code')
  if (!pr.ok) throw new Error(pr.msg || '獲取驗證碼失敗')
  const inner = pr.data
  const b64 =
    inner && typeof inner === 'object' && inner.code != null
      ? inner.code
      : typeof inner === 'string'
        ? inner
        : inner && inner.data
  if (typeof b64 !== 'string' || !String(b64).trim()) {
    throw new Error('驗證碼數據為空')
  }
  return base64ImageToBlob(b64)
}

function loginNeedChangePassword(d) {
  if (!d || typeof d !== 'object') return false
  if (d.needChangePassword === true || d.mustChangePassword === true || d.must_change_password === true) {
    return true
  }
  const v = d.changePwd ?? d.change_pwd ?? d.firstLogin ?? d.first_login ?? d.isFirstLogin ?? d.is_first_login
  if (v === 1 || v === true || v === '1' || v === 'Y' || v === 'y') return true
  const st = d.pwdStatus ?? d.pwd_status
  if (st === 1 || st === '1' || st === 'must_change') return true
  return false
}

function pickLoginCaptchaCode(body) {
  const b = body && typeof body === 'object' ? body : {}
  const raw = b.code ?? b.ValidateCode ?? b.validateCode
  if (raw == null || String(raw).trim() === '') return '-'
  return String(raw).trim()
}

/** 文檔：POST /member/login，Body: type、account、password、驗證碼（部分後端讀 code，部分讀 ValidateCode） */
/** @returns {{ data: *, needChangePassword: boolean }} */
export async function memberLogin(body) {
  const merged = Object.assign({ type: '1', code: '-' }, body || {})
  const captcha = pickLoginCaptchaCode(merged)
  const pr = await mpost('/member/login', {
    ...merged,
    type: merged.type != null ? String(merged.type) : '1',
    code: captcha,
    ValidateCode: captcha,
  })
  if (!pr.ok) throw new Error(pr.msg || '登錄失敗')
  const d = pr.data
  return { data: d, needChangePassword: loginNeedChangePassword(d) }
}

export async function memberChangePassword(body) {
  return ok(await mpost('/member/changePassword', body), '修改密碼失敗')
}

export async function memberLotterys() {
  return ok(await mget('/member/lotterys'), '獲取彩種失敗')
}

export async function memberAccounts() {
  return ok(await mget('/member/accounts'), '獲取賬戶失敗')
}
export async function memberLogout() {
  return ok(await mget('/member/userlogout'), '獲取賬戶失敗')
}

export async function memberNotice() {
  return ok(await mget('/member/notice'), '獲取公告失敗')
}

export async function memberPeriod(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/period' + (q ? '?' + q : '')), '獲取獎期失敗')
}

export async function memberLastResult(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/lastResult' + (q ? '?' + q : '')), '獲取開獎失敗')
}

export async function memberOdds(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/odds' + (q ? '?' + q : '')), '獲取賠率失敗')
}

export async function memberDayResult(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/dayResult' + (q ? '?' + q : '')), '獲取每日開獎失敗')
}

export async function memberBets(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/bets' + (q ? '?' + q : '')), '獲取下注明細失敗')
}

export async function memberHistory(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/history' + (q ? '?' + q : '')), '獲取歷史失敗')
}

export async function memberInfo(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/info' + (q ? '?' + q : '')), '獲取個人資訊失敗')
}

export async function memberDresult(params) {
  const q = new URLSearchParams(params || {}).toString()
  const pr = await mget('/member/dresult' + (q ? '?' + q : ''))
  if (!pr.ok) throw new Error('獲取開獎結果失敗')
  return pr
}

export async function memberFastBetUserData(params) {
  const q = new URLSearchParams(params || {}).toString()
  return ok(await mget('/member/fastBet/user/data' + (q ? '?' + q : '')), '獲取快投失敗')
}

export async function memberBet(body) {
  return ok(await mpost('/member/bet', body), '下注失敗')
}
export async function memberTrack(body) {
  return ok(await mpost('/member/track', Object.assign({}, body, { qudao: 1 })), '下注失敗')
}
